import useModal, { ModalProps } from '@carvana/ds-modal';
import React, { useContext, useEffect, useRef } from 'react';
import BackstopContext from '|/context/backstop-context';
import { useNavigationBlockContext } from '|/context/navigation-block-context';
import useAnalyticsContext from '|/hooks/use-analytics-context';
import useBrowserBackClose from '|/hooks/use-browser-back-close';
import { ModalEvents, ModalNames } from './modal-events';

type EventedModalProps = ModalProps & {
  analyticsId: ModalNames;
  children: React.ReactNode;
  isOpen: boolean;
  eventMetadata?: Record<string, any>;
  // Modals that wire their own useBrowserBackClose (e.g. the embedded appraisal modal) opt out of
  // the experiment back-close guard so the key isn't registered twice. Presence is still tracked.
  selfManagesBackClose?: boolean;
};

const EventedModal = ({
  analyticsId,
  children,
  isOpen,
  eventMetadata,
  onClose,
  selfManagesBackClose = false,
  ...restProps
}: EventedModalProps) => {
  const { Modal, openModal, closeModal } = useModal();
  const { trackEvent } = useAnalyticsContext();
  const { isBackstopTreatment } = useContext(BackstopContext);
  const { addOpenModal, removeOpenModal } = useNavigationBlockContext();
  const prevIsOpenRef = useRef<boolean>(false);
  // Closed must name the modal Opened named. A modal may change analyticsId while open —
  // transfer-method derives it from a sub-view state it resets one render before isOpen flips — so
  // the live value at close time is the reset one, not the sub-view the user was actually looking at.
  const openedIdRef = useRef(analyticsId);

  useEffect(() => {
    const prevIsOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (isOpen && !prevIsOpen) {
      openedIdRef.current = analyticsId;
      openModal();
      trackEvent(ModalEvents.Opened, { modal: analyticsId, ...eventMetadata });
    } else if (!isOpen && prevIsOpen) {
      trackEvent(ModalEvents.Closed, { modal: openedIdRef.current });
      closeModal();
    }
  }, [isOpen, openModal, closeModal, trackEvent, analyticsId, eventMetadata]);

  // Presence: every open modal registers, in both buckets and regardless of selfManagesBackClose,
  // so pp_browser_nav_button's modalOpen/modalName are accurate everywhere.
  useEffect(() => {
    if (!isOpen) return undefined;
    addOpenModal(analyticsId);
    return () => removeOpenModal(analyticsId);
  }, [isOpen, analyticsId, addOpenModal, removeOpenModal]);

  // Guard: arm the browser-Back close only in treatment for non-opted-out modals. Modals with no
  // onClose are deliberately non-dismissible — arming them would consume the user's Back on a
  // handler that does nothing, so the press is silently swallowed. The callback just closes; the
  // pp_browser_nav_button event is owned by the coordinator, not fired here.
  useBrowserBackClose(
    analyticsId,
    isOpen && isBackstopTreatment && !selfManagesBackClose && !!onClose,
    () => onClose?.()
  );

  return (
    <Modal onClose={onClose} {...restProps}>
      {children}
    </Modal>
  );
};

export default EventedModal;
